<script setup>
import isColorBright from "./util/isColorBright";
import { StepperSupportAPI } from "@/util/generated/api-public";
import { useTheme } from "vuetify";
import { useAuthStore } from "@/stores/authStore";
import { useContextStore } from "@/stores/contextStore";
import { useMessageStore } from "@/stores/messageStore";
import { useLoadingStore } from "@/stores/loadingStore";

const theme = useTheme();
const authStore = useAuthStore();
const contextStore = useContextStore();
const messageStore = useMessageStore();
const loadingStore = useLoadingStore();
const stepsStore = useStepsStore();
const route = useRoute();

const isStepperAvailable = ref(null);

// Computed properties
const event = computed(() => {
  if (!authStore.me) {
    return contextStore.event;
  }
  return authStore.me.event_event;
});

const isPrimaryColorBright = computed(() => {
  return isColorBright(theme.themes.value.light.colors.primary);
});

const isSecondaryColorBright = computed(() => {
  return isColorBright(theme.themes.value.light.colors.secondary);
});

const isAccentColorBright = computed(() => {
  return theme.themes.value.light.colors.accent ? isColorBright(theme.themes.value.light.colors.accent) : false;
});

// Watchers
watch(
  () => route.path, // Watch route path or full route
  async () => {
    await detectStepperSupport();
  }
);

// Lifecycle hooks
onMounted(async () => {
  await detectStepperSupport();
});

// Methods
async function detectStepperSupport() {
  if (window.cordova?.platformId && route.meta.systembar && isStepperAvailable.value == null) {
    isStepperAvailable.value = await stepsStore.isAvailable();
    await StepperSupportAPI.reportStepperSupport({
      device_id: window.device && window.device.uuid,
      version: window.device && window.device.version,
      manufacturer: window.device && window.device.manufacturer,
      model: window.device && window.device.model,
      stepper_supported: isStepperAvailable.value,
    });
  }
}
</script>
<template>
  <v-app
    ref="app"
    full-height
    :class="{
      'primary-color-is-bright': isPrimaryColorBright,
      'secondary-color-is-bright': isSecondaryColorBright,
      'accent-color-is-bright': isAccentColorBright,
    }"
  >
    <Theme v-model="event" />
    <v-snackbar
      v-for="(message, index) in messageStore.messages"
      :key="index"
      :color="message.type || 'info'"
      :model-value="message.message"
      location="top"
      :timeout="message.timeout || 7000"
      class="mt-5"
    >
      {{ message.key ? $t(message.key, message.params) : message.message }}
      <v-icon color="white" v-if="message.reactionAddons">{{ message.reactionAddons.type }}</v-icon>
    </v-snackbar>
    <v-overlay :model-value="loadingStore.loading" opacity="0.7" z-index="2000" class="justify-center align-center" persistent>
      <v-progress-circular indeterminate color="primary" size="64"></v-progress-circular>
    </v-overlay>
    <router-view />
    <template v-if="route.meta.systembar">
      <v-divider />
      <div class="d-flex text-grey-darken-3 text-caption px-4 py-1" style="background-color: #fff !important">
        <div>© {{ new Date().getFullYear() }} Alvarum</div>
        <v-spacer />
        <div v-if="isStepperAvailable" class="pr-2">
          <v-icon color="success" class="mt-n1">mdi-check-bold</v-icon>
          {{ $t("system.stepper.available") }}
        </div>
        <div v-else-if="isStepperAvailable === false" class="text--disabled pr-2">
          {{ $t("system.stepper.notavailable") }}
        </div>
      </div>
    </template>
  </v-app>
</template>
<style>
html,
body {
  min-height: var(--safe-viewport-height);
}

.show-only-on-hover {
  opacity: 0;
}

td:hover .show-only-on-hover {
  opacity: 1;
}

#app {
  background: #ebeded;
  min-height: var(--safe-viewport-height);
}

.v-application__wrap {
  min-height: var(--safe-viewport-height) !important;
}

.v-navigation-drawer--is-mobile {
  height: 100% !important;
  height: -webkit-fill-available !important;
}
</style>
